import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, Mail, Loader2 } from "lucide-react";

const sel = "h-10 px-3 rounded-lg border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600";

export default function OrdersExportBar({ opId, setOpId, gId, setGId }) {
  const [ops, setOps] = useState([]);
  const [grossistes, setGrossistes] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/operations").then((r) => setOps(r.data)).catch(() => {});
    api.get("/grossistes").then((r) => setGrossistes(r.data)).catch(() => {});
  }, []);

  const params = { ...(opId && { operation_id: opId }), ...(gId && { grossiste_id: gId }) };
  const selectedG = grossistes.find((g) => g.id === gId);

  const download = async () => {
    setBusy("xls");
    try {
      const r = await api.get("/admin/orders/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commandes_grossistes_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Fichier Excel téléchargé");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Aucune commande pour ces filtres" : "Échec de l'export");
    } finally { setBusy(""); }
  };

  const email = async (send_to) => {
    setBusy(send_to);
    try {
      const { data } = await api.post("/admin/orders/export/email", { ...params, send_to });
      toast.success(`Export envoyé à ${data.to}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const btn = "inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold transition-colors duration-200 disabled:opacity-50";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5 flex flex-wrap items-end gap-3" data-testid="orders-export-bar">
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">Opération</label>
        <select value={opId} onChange={(e) => setOpId(e.target.value)} className={`${sel} w-64`} data-testid="export-filter-operation">
          <option value="">Toutes les opérations</option>
          {ops.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-slate-500 mb-1">Grossiste</label>
        <select value={gId} onChange={(e) => setGId(e.target.value)} className={`${sel} w-56`} data-testid="export-filter-grossiste">
          <option value="">Tous les grossistes</option>
          {grossistes.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      <div className="flex flex-wrap gap-2 ml-auto">
        <button onClick={download} disabled={!!busy} data-testid="export-excel-btn" className={`${btn} bg-emerald-600 hover:bg-emerald-700 text-white`}>
          {busy === "xls" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Télécharger Excel
        </button>
        <button onClick={() => email("me")} disabled={!!busy} data-testid="export-email-me-btn" className={`${btn} border border-slate-300 hover:bg-slate-50 text-slate-700`}>
          {busy === "me" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} M'envoyer par email
        </button>
        <button onClick={() => email("grossiste")} disabled={!!busy || !selectedG?.email} data-testid="export-email-grossiste-btn"
          title={!gId ? "Choisissez un grossiste" : !selectedG?.email ? "Ce grossiste n'a pas d'email" : ""}
          className={`${btn} border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 text-indigo-700`}>
          {busy === "grossiste" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Envoyer au grossiste
        </button>
      </div>
    </div>
  );
}
